import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Package,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  CreditCard,
  RefreshCw,
} from "lucide-react";
import { API } from "@/App";
import { format, differenceInDays } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

export default function MyPackagesModule({ user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages"]);

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState([]);

  useEffect(() => {
    if (user?.user_id) {
      fetchMyPackages();
    }
  }, [user]);

  const fetchMyPackages = async () => {
    try {
      // First get member info for current user
      const memberRes = await fetch(`${API}/members/by-user/${user.user_id}`, {
        credentials: "include",
      });
      
      if (memberRes.ok) {
        const memberData = await memberRes.json();
        setMemberships(memberData);
        
        // Get packages for each membership
        const allPackages = [];
        for (const member of memberData) {
          const pkgRes = await fetch(`${API}/members/${member.member_id}/packages`, {
            credentials: "include",
          });
          if (pkgRes.ok) {
            const pkgs = await pkgRes.json();
            // Add business info to each package
            for (const pkg of pkgs) {
              pkg.business_name = member.business_name || t("common:businessLabel");
              pkg.member_id = member.member_id;
            }
            allPackages.push(...pkgs);
          }
        }
        setPackages(allPackages);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPackageStatus = (pkg) => {
    const endDate = new Date(pkg.end_date);
    const today = new Date();
    const daysLeft = differenceInDays(endDate, today);
    
    if (pkg.remaining_sessions <= 0) {
      return { label: t('common:completed'), color: "bg-gray-100 text-gray-600", icon: CheckCircle };
    }
    if (daysLeft < 0) {
      return { label: t("common:expired"), color: "bg-red-100 text-red-600", icon: AlertCircle };
    }
    if (daysLeft <= 7) {
      return { label: "Son Hafta", color: "bg-orange-100 text-orange-600", icon: Clock };
    }
    return { label: t('admin:active'), color: "bg-green-100 text-green-600", icon: CheckCircle };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">Paketlerim</h2>
          <p className="text-grey text-sm">{t("packages:satinAldiginizPaketleriGoruntuleyin")}</p>
        </div>
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={fetchMyPackages}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Yenile
        </Button>
      </div>

      {/* Active Packages */}
      {packages.length > 0 ? (
        <div className="grid gap-4">
          {packages.map((pkg) => {
            const status = getPackageStatus(pkg);
            const StatusIcon = status.icon;
            const endDate = new Date(pkg.end_date);
            const startDate = new Date(pkg.start_date);
            const totalDays = differenceInDays(endDate, startDate);
            const daysUsed = differenceInDays(new Date(), startDate);
            const daysProgress = Math.min(100, Math.max(0, (daysUsed / totalDays) * 100));
            
            return (
              <Card key={pkg.member_package_id} className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Package Info */}
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-navy/10 rounded-xl flex items-center justify-center">
                        <Package className="w-6 h-6 text-navy" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-navy">{pkg.business_name}</h3>
                          <Badge className={status.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                        <p className="text-grey text-sm mt-1">
                          {format(startDate, "dd/MM/yyyy", { locale: getDateLocale() })} - {format(endDate, "dd/MM/yyyy", { locale: getDateLocale() })}
                        </p>
                      </div>
                    </div>

                    {/* Sessions */}
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-navy">{pkg.remaining_sessions}</p>
                        <p className="text-xs text-grey">{t("members:remainingSessions")}</p>
                      </div>
                      <div className="w-px h-10 bg-gray-200" />
                      <div className="text-center">
                        <p className="text-2xl font-bold text-grey-600">
                          {differenceInDays(endDate, new Date()) > 0 
                            ? differenceInDays(endDate, new Date()) 
                            : 0}
                        </p>
                        <p className="text-xs text-grey">{t("packages:kalanGun")}</p>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-grey mb-1">
                      <span>{t("common:sureİlerlemesi")}</span>
                      <span>{Math.round(daysProgress)}%</span>
                    </div>
                    <Progress value={daysProgress} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Package className="w-12 h-12 text-grey mx-auto mb-4" />
            <h3 className="font-semibold text-navy mb-2">{t("packages:henuzPaketinizYok")}</h3>
            <p className="text-grey text-sm mb-4">
              Bir spor salonuna üye olarak paket satın alabilirsiniz.
            </p>
            <Button className="bg-navy hover:bg-navy/90 rounded-xl">
              <CreditCard className="w-4 h-4 mr-2" />
              Salon Bul
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Memberships Summary */}
      {memberships.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">{t("common:myMemberships")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {memberships.map((membership) => (
                <div
                  key={membership.member_id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                >
                  <div>
                    <p className="font-medium text-navy">{membership.business_name || t("common:businessLabel")}</p>
                    <p className="text-xs text-grey">Üye No: {membership.member_no}</p>
                  </div>
                  <Badge variant="secondary">
                    {membership.is_active ? t('admin:active') : t('common:passive')}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
